import React from "react";
import { Link } from "react-router-dom";
import ArticleLayout, { H2, DataTable } from "./ArticleLayout";

export default function ArticleRetire500k() {
  return (
    <ArticleLayout
      meta={{
        slug: "retire-with-500k",
        hub: "Retirement Calculators",
        time: "8 min read",
        title: "Can I Retire With $500,000? An Honest Answer | Centennial",
        seoDescription:
          "Can you retire on $500k? Honest math: at a 3.5% withdrawal rate that's about $17,500/year plus Social Security. When it works, when it doesn't, and how to test your own plan free.",
        h1: "Can I Retire With $500,000?",
        hook: "It's one of the most searched retirement questions in America — and most answers dodge it. Here's the honest math.",
        quickAnswer:
          "Yes, many people retire on $500,000 — but usually because Social Security does the heavy lifting. At a sustainable 3.5% withdrawal rate, $500k produces about $17,500 per year. Add a typical $23,000 Social Security benefit and a couple claiming two benefits can reach $55,000–$70,000 of annual income. It works when your spending is modest, your house is paid off, and you delay claiming; it fails when withdrawals creep above 5% or retirement starts before Medicare at 65.",
      }}
    >
      <H2>What $500,000 actually generates</H2>
      <p>
        The starting point is the withdrawal rate — the percentage of your savings you draw in
        year one, then adjust for inflation. For a retirement that could last 30+ years, research
        (Bengen's SAFEMAX work and the Trinity Study) supports roughly 3.5–4%. For longer
        horizons — the kind Centennial models to age 100 — 3.0–3.5% is safer.
      </p>
      <DataTable
        head={["Withdrawal rate", "Annual income from $500k", "Survival odds (30+ yrs)"]}
        rows={[
          ["3.0%", "$15,000", "Very high, even to age 100"],
          ["3.5%", "$17,500", "High"],
          ["4.0%", "$20,000", "Good for 30 years, weaker beyond"],
          ["5.0%", "$25,000", "Risky — sequence-of-returns exposure"],
          ["6.0%", "$30,000", "High chance of running out"],
        ]}
      />
      <p>
        Read why the classic rule breaks on long horizons in{" "}
        <Link className="text-[#2B5339] underline" to="/learn/four-percent">Is the 4% rule still safe?</Link>
      </p>

      <H2>Social Security changes everything</H2>
      <p>
        The average retired-worker benefit in 2026 is roughly $1,900–$2,000/month — about
        $23,000/year (source: Social Security Administration). A married couple with two
        benefits might collect $45,000+ before touching savings. That's why "$500k + Social
        Security" is a workable retirement for millions, while "$500k alone" rarely is.
      </p>
      <p>
        Claiming age matters enormously — see our{" "}
        <Link className="text-[#2B5339] underline" to="/learn/social-security-claiming-age">
          claiming-age comparison (62 vs 67 vs 70)
        </Link>.
      </p>

      <H2>When $500k works</H2>
      <p>
        A paid-off home. Annual spending near $50,000–$60,000 (couple) or $35,000–$45,000
        (single), including taxes and healthcare. Retirement at 65+ so Medicare handles health
        insurance. Willingness to trim withdrawals after a bad market year — flexible
        "guardrails" strategies measurably improve survival odds.
      </p>

      <H2>When it doesn't</H2>
      <p>
        Retiring at 58 with private health insurance costing $1,000+/month. Carrying a mortgage
        into retirement. Needing $80,000/year of spending. Withdrawing 5–6% and hitting a 2008-style
        crash in the first five years —{" "}
        <Link className="text-[#2B5339] underline" to="/learn/sequence-risk">sequence-of-returns risk</Link>{" "}
        is the killer here.
      </p>

      <H2>Test it on your own numbers — free</H2>
      <p>
        Generic articles can't know your age, spending, or Social Security estimate. The
        Centennial calculator runs your actual plan through 5,000 market simulations plus the
        1929, 1973, 2000, and 2008 sequences — free, no signup. Then work through the{" "}
        <Link className="text-[#2B5339] underline" to="/learn/readiness-checklist">50-point readiness checklist</Link>{" "}
        to catch what the numbers miss.
      </p>
    </ArticleLayout>
  );
}
